import { Box, Loader, Text, Center, useMantineTheme } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import { useEffect, forwardRef } from 'react';
import { IconRefresh } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface InfiniteScrollProps {
  children: React.ReactNode;
  onLoadMore: () => void;
  hasMore: boolean;
  loading?: boolean;
  loadingComponent?: React.ReactNode;
  endComponent?: React.ReactNode;
  threshold?: number;
  rootMargin?: string;
  direction?: 'down' | 'up';
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
}

const InfiniteScroll = forwardRef<HTMLDivElement, InfiniteScrollProps>(
  (
    {
      children,
      onLoadMore,
      hasMore,
      loading = false,
      loadingComponent,
      endComponent,
      threshold = 0.5,
      rootMargin = '100px',
      direction = 'down',
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const { ref: intersectionRef, entry } = useIntersection({
      root: null,
      threshold,
      rootMargin,
    });

    useEffect(() => {
      if (entry?.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    }, [entry?.isIntersecting, hasMore, loading, onLoadMore]);

    const setRefs = (node: HTMLDivElement | null) => {
      intersectionRef(node);
    };

    const defaultLoadingComponent = (
      <Center py="xl">
        <Loader
          size="sm"
          color={gradient?.from}
        />
        <Text ml="sm" size="sm" c="dimmed" style={accessibilityStyles}>
          Loading more...
        </Text>
      </Center>
    );

    const defaultEndComponent = (
      <Center py="xl">
        <Text size="sm" c="dimmed" style={accessibilityStyles}>
          No more items to load
        </Text>
      </Center>
    );

    const sentinel = (
      <Box ref={setRefs} style={{ minHeight: 1 }}>
        {loading && (loadingComponent || defaultLoadingComponent)}
        {!loading && !hasMore && (endComponent || defaultEndComponent)}
      </Box>
    );

    return (
      <Box ref={ref}>
        {direction === 'up' && sentinel}
        {children}
        {direction === 'down' && sentinel}
      </Box>
    );
  }
);

InfiniteScroll.displayName = 'InfiniteScroll';

export default InfiniteScroll;
